<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters } from 'dashboard/composables/store';
import { v4 as uuidv4 } from 'uuid';
import PageHeader from '../../../SettingsSubPageHeader.vue';

const emit = defineEmits(['next']);

const store = useStore();

const channelName = ref('');
const webhookUrl = ref('');
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const getters = useStoreGetters();

const currentUser = computed(() => getters.getCurrentUser.value);
const currentAccount = computed(() => getters.getCurrentAccount.value);

const shouldBeWebhookUrl = (value = '') =>
  value ? value.startsWith('http') : true;

const rules = {
  channelName: { required },
  webhookUrl: { shouldBeWebhookUrl },
};

// Evolution options:
const formData = ref({
  alwaysOnline: false,
  ignoreGroups: true,
  readMessages: true,
  signMessage: true,
  reopenConversation: true,
  importContacts: true,
});

// Loading states
const isSubmitting = ref(false);
const submissionError = ref(null);

const v$ = useVuelidate(rules, { channelName, webhookUrl });

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    isSubmitting.value = true;
    const instanceName = uuidv4();
    // create api inbox
    const apiChannel = await store.dispatch('inboxes/createChannel', {
      name: channelName.value,
      channel: {
        type: 'api',
        webhook_url: `${import.meta.env.VITE_EVOLUTION_URL}/chatwoot/webhook/${instanceName}`,
      },
      integration_type: 'Evolution',
      integration_id: instanceName,
    });

    const payload = {
      instanceName,
      qrcode: true,
      integration: 'WHATSAPP-BAILEYS',
      groupsIgnore: formData.value.ignoreGroups,
      alwaysOnline: formData.value.alwaysOnline,
      readMessages: formData.value.readMessages,
      readStatus: true,
      syncFullHistory: false,
      chatwootAccountId: String(currentAccount.value.id),
      chatwootToken: currentUser.value.access_token,
      chatwootUrl: import.meta.env.VITE_HOST,
      chatwootSignMsg: formData.value.signMessage,
      chatwootReopenConversation: formData.value.reopenConversation,
      chatwootConversationPending: true,
      chatwootImportContacts: false,
      chatwootNameInbox: channelName.value,
      chatwootMergeBrazilContacts: true,
      chatwootDaysLimitImportMessages: 10,
      chatwootOrganization: currentAccount.value.name,
    };

    // Create instance
    const response = await fetch(
      `${import.meta.env.VITE_EVOLUTION_URL}/instance/create`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_EVOLUTION_TOKEN,
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.status === 403) {
      useAlert(this.$t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.ERRORS.CONFLICT'));
      throw new Error(
        this.$t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.ERRORS.CONFLICT')
      );
    }
    if (!response.ok) throw new Error('API Error');

    submissionError.value = '';

    emit('next', { instanceName, inboxId: apiChannel.id });
  } catch (error) {
    useAlert('Erro ao criar caixa de entrada');
  }
};
</script>

<template>
  <PageHeader
    header-title="Criar caixa de entrada"
    header-content="Integre o seu whatsapp não oficial com o Sendd!"
  />
  <form class="flex flex-wrap mx-0" @submit.prevent="createChannel">
    <div class="w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <label :class="{ error: v$.channelName.$error }">
        {{ $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.LABEL') }}
        <input
          v-model="channelName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
          "
          @blur="v$.channelName.$touch"
        />
        <span v-if="v$.channelName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.API_CHANNEL.CHANNEL_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="space-y-2 w-[65%] flex-shrink-0 flex-grow-0 max-w-[65%]">
      <p>{{ $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELD_TITLE') }}</p>
      <label class="flex items-center space-x-2">
        <woot-switch id="sla_bh" v-model="formData.alwaysOnline" />
        <span>{{
          $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELDS.ALWAYS_ONLINE')
        }}</span>
      </label>

      <label class="flex items-center space-x-2">
        <woot-switch id="sla_bh" v-model="formData.ignoreGroups" />
        <span>{{
          $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELDS.IGNORE_GROUPS')
        }}</span>
      </label>

      <label class="flex items-center space-x-2">
        <woot-switch id="sla_bh" v-model="formData.readMessages" />
        <span>{{
          $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELDS.READ_MESSAGES')
        }}</span>
      </label>

      <label class="flex items-center space-x-2">
        <woot-switch id="sla_bh" v-model="formData.signMessage" />
        <span>{{
          $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELDS.SIGN_MESSAGE')
        }}</span>
      </label>

      <label class="flex items-center space-x-2">
        <woot-switch id="sla_bh" v-model="formData.reopenConversation" />
        <span>{{
          $t('EVOLUTION_MGMT.CREATE_FLOW.CONFIG.FIELDS.REOPEN_CONVERSATION')
        }}</span>
      </label>
    </div>

    <div class="w-full mt-4">
      <woot-submit-button
        :loading="uiFlags.isCreating"
        :button-text="$t('INBOX_MGMT.ADD.API_CHANNEL.SUBMIT_BUTTON')"
      />
    </div>
  </form>
</template>
